import { CheckCircle as CheckCircleIcon, Edit as EditIcon, SyncProblem as SyncProblemIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { Platform } from '../../../services/integration.types';
import { Integration } from '../../../services/mocks/data/integrations';
import { formatCurrency, formatTimeAgo } from '../../../utils/formatters';

interface ExchangeApiPreviewProps {
    integration: Integration;
    platform: Platform;
    onEdit: () => void;
}

const ExchangeApiPreview: React.FC<ExchangeApiPreviewProps> = ({ integration, platform, onEdit }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    // Add a helper function to get the appropriate API key label based on platform
    const getApiKeyLabel = (platformName: string, t: (key: string, vars?: any) => string): string => {
        switch (platformName) {
            case 'bitpanda':
                return t('integrations.exchange.api.apiJobName');
            default:
                return t('integrations.exchange.api.apiKey');
        }
    };

    const getStatusConfig = (status: Integration['status']['status']) => {
        switch (status) {
            case 'in_progress':
                return {
                    label: t('integrations.exchange.preview.status.syncing'),
                    color: '#ffca27',
                    icon: <CircularProgress size={16} thickness={6} sx={{ color: '#ffca27' }} />,
                };
            case 'done':
                return {
                    label: t('integrations.exchange.preview.status.synced'),
                    color: theme.palette.success.main,
                    icon: <CheckCircleIcon sx={{ color: theme.palette.success.main }} />,
                };
            case 'error':
                return {
                    label: t('integrations.exchange.preview.status.error'),
                    color: theme.palette.error.main,
                    icon: <SyncProblemIcon sx={{ color: theme.palette.error.main }} />,
                };
            default:
                return {
                    label: t('integrations.exchange.preview.status.unknown'),
                    color: theme.palette.text.secondary,
                    icon: null,
                };
        }
    };

    const statusConfig = getStatusConfig(integration.status.status);

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Card variant="outlined">
                    <CardContent>
                        {integration.apiKey && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {getApiKeyLabel(platform.name, t)}:
                                </Typography>
                                <Typography 
                                    variant="body2"
                                    sx={{ 
                                        fontFamily: 'monospace',
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 0.5,
                                        fontSize: '0.8rem',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {integration.apiKey}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                {statusConfig.icon}
                                <Typography variant="body2" color="text.secondary">
                                    {t('integrations.exchange.preview.status.label')}
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ 
                                color: statusConfig.color, 
                                fontWeight: 'medium',
                                wordBreak: 'break-word'
                            }}>
                                {integration.status.message ? integration.status.message : statusConfig.label}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('integrations.exchange.preview.lastSynced')}
                            </Typography>
                            <Typography variant="body1">
                                {(() => {
                                    const time = formatTimeAgo(integration.endTime, t);
                                    // If the time contains a number followed by "Minute", ensure proper plural form
                                    return time.replace(/(\d+) Minute(?!n)/, "$1 Minuten");
                                })()}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('integrations.exchange.preview.transactions')}
                            </Typography>
                            <Typography variant="body1">{integration.transactionsCount}</Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('integrations.exchange.preview.value')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatCurrency(integration.value)}
                            </Typography>
                        </Box>

                        <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
                            {t('integrations.exchange.preview.editButton')}
                        </Button>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5, 
                    p: 2,
                    height: '100%',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1
                }}>
                    <Typography variant="body2" sx={{ 
                        pb: 1,
                        color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'inherit'
                    }}>
                        {t('integrations.exchange.preview.infoDescription', { platform: platform.display_name })}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                        {t('integrations.exchange.preview.securityNote')}
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );
};

export default ExchangeApiPreview;
